import './styles/JobManagement.css';

import { Fragment, PureComponent, createRef } from 'react';

import {
  jobCancel,
  jobStop,
  readAllJobs,
  readJob,
  setStopJobId,
} from './actions';
import {
  makeSelectCancelling,
  makeSelectJobs,
  makeSelectReading,
  makeSelectStopJobAction,
  makeSelectStopJobId,
  makeSelectStopping,
} from './selectors';

import { Tooltip } from '@ipfabric/ui-components/Tooltip/Tooltip';
import { date, untitledSnapshotName } from '@ipfabric/utils';
import classNames from 'classnames';
import { Button } from 'components/Button/Button';
import { ButtonAsA } from 'components/Button/ButtonAsA';
import { ConfirmDialog } from 'containers/ConfirmDialog/ConfirmDialog';
import { toggleConfirmDialog } from 'containers/ConfirmDialog/actions';
import { Permissions } from 'containers/Permissions/Permissions';
import get from 'lodash/get';
import isNumber from 'lodash/isNumber';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import Transition from 'react-transition-group/Transition';
import { createStructuredSelector } from 'reselect';
import handleEvent from 'utils/handleEvent';
import { withAppProps } from 'utils/react/withAppProps';
import { ClickOutsideDropdownHandler } from '../ClickOutsideDropdownHandler';
import {
  JOB_ACTIONS,
  JOB_STATUSES,
  STOP_JOB_DISCOVERY_DIALOGUE_NAME,
} from './constants';

@withAppProps
class JobManagement extends PureComponent {
  ref = createRef();
  static propTypes = {
    auth: PropTypes.object.isRequired,
    cancelling: ImmutablePropTypes.list.isRequired,
    dateTimeFormat: PropTypes.string.isRequired,
    timezone: PropTypes.string.isRequired,
    jobs: ImmutablePropTypes.list.isRequired,
    msg: PropTypes.object.isRequired,
    onJobCancel: PropTypes.func.isRequired,
    onJobStop: PropTypes.func.isRequired,
    onReadAllJobs: PropTypes.func.isRequired,
    onReadJob: PropTypes.func.isRequired,
    onSetStopJobId: PropTypes.func.isRequired,
    onToggleConfirmDialog: PropTypes.func.isRequired,
    stopJobAction: PropTypes.string,
    reading: ImmutablePropTypes.list.isRequired,
    stopJobId: PropTypes.string,
    stopping: ImmutablePropTypes.list.isRequired,
    toggle: PropTypes.func.isRequired,
    open: PropTypes.bool.isRequired,
  };

  state = {
    animationIcon: false,
    animationDisplayNumber: '',
    animationBackground: '',
    rightPosition: 'auto',
  };

  componentDidMount() {
    this.fixMenuPosition();
  }

  componentDidUpdate(prevProps) {
    const { jobs, open } = this.props;
    if (jobs !== prevProps.jobs) {
      const animationType = this.getAnimationType(prevProps.jobs, jobs);
      if (animationType !== 'none') {
        if (!this.animating) {
          this.startAnimation(jobs, animationType);
        } else if (
          this.animating &&
          jobs.size > this.state.animationDisplayNumber &&
          !this.unmounting
        ) {
          this.setState(
            {
              animationIcon: false,
              animationDisplayNumber: '',
              animationBackground: '',
            },
            () => {
              setTimeout(() => {
                this.startAnimation(jobs, animationType);
              }, 0);
            },
          );
        }
      }
    }
    if (open !== prevProps.open && open) {
      this.fixMenuPosition();
    }
  }

  componentWillUnmount() {
    this.unmounting = true;
  }

  formatDate = (timestamp) => {
    const { dateTimeFormat, timezone } = this.props;
    return date.formatUtcTimestampInTimeZone(
      timestamp,
      timezone,
      dateTimeFormat,
    );
  };

  renderJobDownloadLink = (job) =>
    job.downloadFile && (
      <a
        href={`${
          // biome-ignore lint/correctness/noUndeclaredVariables: global variable provided by rspack devServer
          API_URL
        }/jobs/${job.id}/download`}
      >
        {this.props.msg.jobManagement.download}
      </a>
    );

  getAnimationType = (prevJobs, jobs) => {
    if (
      jobs.size > prevJobs.size &&
      jobs
        .sort((a, b) => b.get('scheduledAt') - a.get('scheduledAt'))
        .getIn([0, 'status']) === JOB_STATUSES.error
    ) {
      return 'danger';
    }
    if (jobs.size > prevJobs.size) {
      return 'warning';
    }
    if (jobs.size === prevJobs.size) {
      const foundNewErrors = jobs.find((job) => {
        const id = job.get('id');
        const foundPrevJob = prevJobs.find(
          (prevJob) => prevJob.get('id') === id,
        );
        if (foundPrevJob) {
          return (
            job.get('status') !== foundPrevJob.get('status') &&
            job.get('status') === JOB_STATUSES.error
          );
        }
        return false;
      });
      return foundNewErrors ? 'danger' : 'none';
    }
    return 'none';
  };

  getJobName = (job) => {
    const {
      msg: { jobManagement: msg },
    } = this.props;
    if (job.status === JOB_STATUSES.error) {
      const foundMessage = get(msg, ['jobs', 'error', job.action]);
      if (foundMessage) {
        return foundMessage;
      }
    }
    return get(msg, ['jobs', job.action], job.action);
  };

  getJobDetail = (job) => {
    const {
      msg: { jobManagement: msg },
    } = this.props;
    const jobAction = job.action;
    if (job.cron === true && job.status === JOB_STATUSES.scheduled) {
      const formattedDate = this.formatDate(job.scheduledAt);
      return (
        <div className='header-dropdown__content-detail'>{formattedDate}</div>
      );
    }

    const renderJobNameAndTime = (snapshotName, time) => {
      const name =
        snapshotName === null
          ? ''
          : `${snapshotName || untitledSnapshotName} / `;
      return (
        <div className='header-dropdown__content-detail font-semibold'>
          {name}
          {this.formatDate(time)}
        </div>
      );
    };

    const renderJobDetailContent = (jobDetail, snapshot) => {
      if (isNumber(jobDetail)) {
        return renderJobNameAndTime(snapshot?.name, jobDetail);
      }
      return snapshot
        ? renderJobNameAndTime(jobDetail, snapshot.tsStart)
        : jobDetail;
    };

    const jobDetail = job.args?.jobDetail;
    const snapshot = job.args?.snapshot;
    switch (jobAction) {
      case JOB_ACTIONS.snapshotClone:
      case JOB_ACTIONS.snapshotDelete:
      case JOB_ACTIONS.snapshotLoad:
      case JOB_ACTIONS.snapshotUnload: {
        return (
          <div className='job-management__content-detail header-dropdown__content-detail'>
            <div className='job-management__name-detail'>
              {renderJobDetailContent(jobDetail, snapshot)}
            </div>
            <div className='job-management__created-at mr-3'>
              {this.formatDate(job.createdAt)}
            </div>
          </div>
        );
      }
      case JOB_ACTIONS.snapshotVerify: {
        return (
          <div
            className='job-management__content-detail header-dropdown__content-detail'
            aria-label='Snapshot Verify'
          >
            <div className='job-management__name-detail'>{jobDetail}</div>
            <div className='job-management__created-at mr-3'>
              {this.formatDate(job.createdAt)}
            </div>
          </div>
        );
      }
      case JOB_ACTIONS.snapshotDownload: {
        return (
          <>
            {snapshot && renderJobNameAndTime(snapshot.name, snapshot.tsStart)}
            <div
              className='header-dropdown__content-detail flex justify-between'
              aria-label='Snapshot Download'
            >
              <div className='mr-3'>{this.formatDate(job.createdAt)}</div>
              {this.renderJobDownloadLink(job)}
            </div>
          </>
        );
      }
      case JOB_ACTIONS.configurationExport: {
        return (
          <>
            <div
              className='header-dropdown__content-detail'
              aria-label='Configuration Export'
            >
              <div className='mr-3'>{this.formatDate(job.createdAt)}</div>
              {this.renderJobDownloadLink(job)}
            </div>
          </>
        );
      }
      case JOB_ACTIONS.techsupport: {
        return (
          <div
            className='header-dropdown__content-detail'
            aria-label='Techsupport'
          >
            <div className='mr-3'>{this.formatDate(job.createdAt)}</div>
            {this.renderJobDownloadLink(job)}
          </div>
        );
      }
      case JOB_ACTIONS.toggleMcp: {
        const toggleMcpText =
          job.args?.enable === true
            ? msg.toggleMcpEnabled
            : msg.toggleMcpDisabled;
        return (
          <>
            <div
              className='header-dropdown__content-detail'
              aria-label='MCP Toggle'
            >
              {toggleMcpText}
            </div>
            <div className='header-dropdown__content-detail'>
              <div className='mr-3'>{this.formatDate(job.createdAt)}</div>
            </div>
          </>
        );
      }
      case JOB_ACTIONS.aimFlowPathLookup: {
        // The triggering view supplies `callbackUrl` (an internal relative path
        // to the filtered Inventory devices view); the API caller omits it, in
        // which case no link is shown.
        const callbackUrl = job.args?.callbackUrl;
        return (
          <div className='header-dropdown__content-detail flex justify-between'>
            <div className='mr-3'>{this.formatDate(job.createdAt)}</div>
            {callbackUrl && <Link to={callbackUrl}>{msg.viewDevices}</Link>}
          </div>
        );
      }
      default:
        return (
          <>
            {snapshot && renderJobNameAndTime(snapshot.name, snapshot.tsStart)}
            <div className='header-dropdown__content-detail'>
              <div className='mr-3'>{this.formatDate(job.createdAt)}</div>
            </div>
          </>
        );
    }
  };

  getError = (error) => {
    return get(error, 'message', error);
  };

  getJobErrorReason = (job) => {
    const {
      msg: { jobManagement: msg },
    } = this.props;
    const { reason } = job;
    if (job.status === JOB_STATUSES.error && reason) {
      return (
        <div className='text-danger break-words'>
          {get(msg, 'errorReason', '').replace(
            '{reason}',
            this.getError(reason),
          )}
        </div>
      );
    }
    return null;
  };

  getMenuIcon = (hasError, jobs) => {
    if (hasError) {
      return (
        <span className='label label-danger'>
          <i className='fa fa-exclamation' />
        </span>
      );
    }
    return <span className='label label-warning'>{jobs.size}</span>;
  };

  getJobIcon = (job) => {
    if (job.status === JOB_STATUSES.error) {
      return 'fa fa-exclamation-triangle fa-fw';
    }
    if ([JOB_STATUSES.running, JOB_STATUSES.stopping].includes(job.status)) {
      return 'fa-regular fa-spinner fa-spin fa-fw';
    }
    if (job.done) {
      return 'fa fa-check fa-fw';
    }
    return 'fa fa-clock-o fa-fw';
  };

  getJobTitle = (job) => {
    const {
      msg: { jobManagement: msg },
    } = this.props;
    if (job.status === JOB_STATUSES.error) {
      return '';
    }
    if (job.status === JOB_STATUSES.running) {
      return get(msg, 'runningTooltip');
    }
    if (job.status === JOB_STATUSES.stopping) {
      return get(msg, 'stoppingTooltip');
    }
    return get(msg, 'scheduledTooltip');
  };

  getAnimationIcon = (state) => {
    if (state === 'entering' || state === 'entered') {
      return 'ipf-job-management-zoomIn';
    }
    if (state === 'exiting' || state === 'exited') {
      return 'ipf-job-management-zoomOut';
    }
    return '';
  };

  getDeleteConfirmDialogMessages = () => {
    const {
      msg: { jobManagement: msg },
      stopJobAction,
    } = this.props;
    return {
      header: get(
        msg,
        ['stopDialogue', stopJobAction === 'read' ? 'readTitle' : 'title'],
        '',
      ),
      subHeader: get(
        msg,
        [
          'stopDialogue',
          stopJobAction === 'read' ? 'readSubHeader' : 'subHeader',
        ],
        '',
      ),
      btnConfirm: get(msg, [
        'stopDialogue',
        stopJobAction === 'read' ? 'btnClearConfirm' : 'btnConfirm',
      ]),
      btnSecondaryConfirm: get(msg, 'stopDialogue.btnCancel'),
    };
  };

  getContentClassName = (job) => {
    if (job.status === JOB_STATUSES.error) {
      return 'text-danger';
    }
    if (job.status === JOB_STATUSES.done) {
      return 'text-success';
    }
    return '';
  };

  fixMenuPosition = () => {
    const dropdown = document.querySelector('#dropdown-job-management');
    if (dropdown) {
      const { x, width } = dropdown.getBoundingClientRect();
      if (x + width > window.innerWidth && !this.unmounting) {
        this.setState({ rightPosition: 0 });
      }
    }
  };

  startAnimation = (jobs, animationType) => {
    this.animating = true;
    if (!this.unmounting) {
      this.setState(
        {
          animationIcon: true,
          animationDisplayNumber: jobs.filter((job) => {
            if (animationType === 'danger') {
              return job.get('status') === JOB_STATUSES.error;
            }
            return job.get('status') !== JOB_STATUSES.error;
          }).size,
          animationBackground: animationType,
        },
        () => {
          if (this.animationTimeout) {
            clearTimeout(this.animationTimeout);
          }
          this.animationTimeout = setTimeout(() => {
            if (!this.unmounting) {
              this.setState({ animationIcon: false }, () => {
                this.animating = false;
              });
            }
            clearTimeout(this.animationTimeout);
          }, 1500);
        },
      );
    }
  };

  makeHandleJobCancel = (id) => () => {
    const { cancelling, onSetStopJobId, onToggleConfirmDialog } = this.props;
    if (cancelling.indexOf(id) === -1) {
      onSetStopJobId({ id, action: 'cancel' });
      onToggleConfirmDialog(STOP_JOB_DISCOVERY_DIALOGUE_NAME);
      this.props.toggle();
    }
  };

  makeHandleJobStop = (id) => () => {
    const { stopping, onToggleConfirmDialog, onSetStopJobId } = this.props;
    if (stopping.indexOf(id) === -1) {
      onSetStopJobId({ id, action: 'stop' });
      onToggleConfirmDialog(STOP_JOB_DISCOVERY_DIALOGUE_NAME);
    }
  };

  makeHandleReadJob = (id) => () => {
    if (!this.props.reading.includes(id)) {
      this.props.onReadJob({ id });
    }
  };

  handleStopButtonConfirm = handleEvent(() => {
    const {
      stopJobId,
      stopJobAction,
      onJobStop,
      onJobCancel,
      onReadJob,
      onReadAllJobs,
    } = this.props;
    if (stopJobAction === 'stop') {
      onJobStop(stopJobId);
    }
    if (stopJobAction === 'cancel') {
      onJobCancel(stopJobId);
    }
    if (stopJobAction === 'read') {
      if (stopJobId === 'all') {
        onReadAllJobs({ force: true });
      } else {
        onReadJob({ id: stopJobId, force: true });
      }
    }
  });

  handleReadAllJobs = handleEvent(() => {
    this.props.onReadAllJobs({ authId: this.props.auth.id });
  });

  isJobRunning = (job) =>
    job.get('status') === JOB_STATUSES.running ||
    job.get('status') === JOB_STATUSES.stopping;

  renderJobs = (jobs, category) => {
    const {
      auth,
      cancelling,
      msg: { jobManagement: msg },
      reading,
      stopping,
    } = this.props;

    const CancelButton = (btnProps) => (
      <Button variant='tertiary' size='xxs' icon='fa-xmark' {...btnProps} />
    );

    return jobs.toJS().map((job) => (
      <div
        key={job.id}
        className={classNames(
          this.getContentClassName(job),
          'header-dropdown__content-item',
        )}
        // biome-ignore lint/a11y/useSemanticElements: The div is used instead of a semantic element to allow custom styling and behavior while still providing a role of 'listitem' for accessibility.
        role='listitem'
        aria-label={`${this.getJobName(job)} (${category})`}
      >
        <div className='header-dropdown__job-icon-wrapper'>
          <Tooltip content={this.getJobTitle(job)}>
            <i className={this.getJobIcon(job)} />
          </Tooltip>
        </div>
        <div className='job-management__message'>
          <div className='font-semibold'>{this.getJobName(job)}</div>
          {this.getJobDetail(job)}
          {this.getJobErrorReason(job)}
        </div>
        <div className='header-dropdown__action-wraper'>
          {job.stoppable ? (
            <Tooltip content={get(msg, 'stopTooltip')}>
              <CancelButton
                aria-label='Stop'
                onClick={this.makeHandleJobStop(job.id)}
                disabled={stopping.indexOf(job.id) > -1}
                loading={stopping.includes(job.id)}
              />
            </Tooltip>
          ) : null}
          {job.cancellable ? (
            <Tooltip content={get(msg, 'cancelTooltip')}>
              <CancelButton
                aria-label='Cancel'
                onClick={this.makeHandleJobCancel(job.id)}
                loading={cancelling.includes(job.id)}
              />
            </Tooltip>
          ) : null}
          {job.done &&
          job.status !== JOB_STATUSES.error &&
          auth.id === job.userId ? (
            <Permissions requiredScope={['write']}>
              <Tooltip content={get(msg, 'clearTooltip')}>
                <CancelButton
                  aria-label='Clear'
                  onClick={this.makeHandleReadJob(job.id)}
                  loading={reading.includes(job.id)}
                />
              </Tooltip>
            </Permissions>
          ) : null}
          {job.status === JOB_STATUSES.error ? (
            <Tooltip content={get(msg, 'clearTooltip')}>
              <CancelButton
                aria-label='Clear'
                onClick={this.makeHandleReadJob(job.id)}
                loading={reading.includes(job.id)}
              />
            </Tooltip>
          ) : null}
        </div>
      </div>
    ));
  };

  renderTransitionContent = (state) => {
    const { animationDisplayNumber, animationBackground } = this.state;
    return (
      <div
        ref={this.ref}
        className={classNames(
          `alert-${animationBackground}`,
          'animated',
          this.getAnimationIcon(state),
          'header-job-management__animation-icon',
        )}
      >
        <div className='header-job-management__animation-content'>
          {animationDisplayNumber}
        </div>
      </div>
    );
  };

  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: hic sunt dracones
  render() {
    const {
      msg: { jobManagement: msg },
      jobs,
      stopJobId,
    } = this.props;
    const { animationIcon, rightPosition } = this.state;
    const hasError =
      jobs.findIndex((job) => job.get('status') === JOB_STATUSES.error) > -1;
    const running = jobs.some(this.isJobRunning);
    const runningJobs = jobs.filter(
      (job) =>
        ((job.get('cron') && this.isJobRunning(job)) || !job.get('cron')) &&
        !job.get('done'),
    );
    const finishedJobs = jobs.filter((job) => job.get('done'));
    const scheduledJobs = jobs.filter(
      (job) => job.get('cron') && job.get('status') === JOB_STATUSES.scheduled,
    );
    return (
      <ClickOutsideDropdownHandler
        className='dropdown header-dropdown__dropdown-li'
        open={this.props.open}
        toggle={this.props.toggle}
        id='job-management'
      >
        {stopJobId ? (
          <ConfirmDialog
            id={STOP_JOB_DISCOVERY_DIALOGUE_NAME}
            onClickConfirm={this.handleStopButtonConfirm}
            msg={this.getDeleteConfirmDialogMessages()}
            btnConfirmVariant='primary'
            btnConfirmAccent='negative'
            btnCancelVariant='secondary'
            btnCancelAccent='positive'
          />
        ) : null}
        <ButtonAsA
          style={{ position: 'relative' }}
          className='dropdown-toggle'
          onClick={this.props.toggle}
          id='header-job-management-toggle'
          // biome-ignore lint/a11y/useSemanticElements: Why is this rule even a thing - "combobox" isn't even supposed to be used on a select element..
          role='combobox'
          aria-haspopup
          aria-expanded={this.props.open}
          aria-label='Jobs'
          aria-controls='dropdown-job-management'
        >
          <Transition in={animationIcon} timeout={1500} nodeRef={this.ref}>
            {this.renderTransitionContent}
          </Transition>
          <div className='three-cogs fa-1x'>
            <i
              className={`fa fa-gear ${running ? 'fa-spin' : ''} fa-2x fa-fw`}
            />
            <i
              className={`fa fa-gear ${running ? 'fa-spin' : ''} fa-1x fa-fw`}
            />
            <i
              className={`fa fa-gear ${running ? 'fa-spin' : ''} fa-1x fa-fw`}
            />
          </div>

          {jobs?.size ? this.getMenuIcon(hasError, jobs) : null}
        </ButtonAsA>
        <ul
          className={classNames(
            'dropdown-menu dropdown-jobs header-dropdown__dropdown-menu',
            this.props.open && 'dropdown-menu--open',
          )}
          id='dropdown-job-management'
          style={{ right: rightPosition }}
          aria-label='Jobs'
        >
          {jobs?.size ? (
            <Fragment>
              {runningJobs.size ? (
                <div className='header header-dropdown__content-header header-dropdown__content-header--border'>
                  {get(msg, 'jobsQueueTitle')}
                </div>
              ) : null}
              {runningJobs.size
                ? this.renderJobs(runningJobs, 'running')
                : null}
              {finishedJobs.size ? (
                <div className='header header-dropdown__content-header header-dropdown__content-header--border flex items-center justify-between'>
                  {get(msg, 'jobsResultsTitle')}
                  <Button
                    className='float-right'
                    onClick={this.handleReadAllJobs}
                    size='xxs'
                    type='button'
                    variant='secondary'
                  >
                    {get(msg, 'btnClearAll')}
                  </Button>
                </div>
              ) : null}
              {finishedJobs.size
                ? this.renderJobs(finishedJobs, 'finished')
                : null}
              {scheduledJobs.size ? (
                <div className='header header-dropdown__content-header header-dropdown__content-header--border'>
                  {get(msg, 'scheduledTitle')}
                </div>
              ) : null}
              {scheduledJobs.size
                ? this.renderJobs(scheduledJobs, 'scheduled')
                : null}
            </Fragment>
          ) : (
            <div className='header header-dropdown__content-header'>
              {get(msg, 'noJobsRunning')}
            </div>
          )}
        </ul>
      </ClickOutsideDropdownHandler>
    );
  }
}

const mapStateToProps = createStructuredSelector({
  cancelling: makeSelectCancelling(),
  stopping: makeSelectStopping(),
  jobs: makeSelectJobs(),
  reading: makeSelectReading(),
  stopJobAction: makeSelectStopJobAction(),
  stopJobId: makeSelectStopJobId(),
});

function mapDispatchToProps(dispatch) {
  return {
    onJobCancel: (args) => dispatch(jobCancel(args)),
    onJobStop: (args) => dispatch(jobStop(args)),
    onToggleConfirmDialog: (args) => dispatch(toggleConfirmDialog(args)),
    onSetStopJobId: (args) => dispatch(setStopJobId(args)),
    onReadJob: (args) => dispatch(readJob(args)),
    onReadAllJobs: (args) => dispatch(readAllJobs(args)),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(JobManagement);
